import type { DraftSet } from './state/loggerDraftTypes';

// LG-28 — the one predicate behind "this prescribed AMRAP cannot be logged yet".
//
// It has TWO consumers that must never disagree:
//   - useTimelineLogger, which refuses the completing tap while it holds;
//   - FocusPanelInputs, which shows the "type the reps you did" cue.
// Two copies of the same condition would eventually drift into a logger that
// refuses to complete a set while showing no reason, or explains a rule it no
// longer enforces — the worst version of both. One function, both callers.
//
// WHY the rule exists: a prescribed AMRAP hydrates with an EMPTY reps cell so
// the prescription can never be accepted as the measurement. An empty cell is
// unsaveable — buildPayload parses it, gets NaN and SKIPS the set — so allowing
// the completion would trade a mis-measured set for a silently DELETED one.
//
// Deliberately narrow: only an AMRAP set, prescribed or with the chip on
// (#539: the chip empties the reps cell too, and an AMRAP with no reps
// measures nothing). An ordinary set with an empty reps cell is the athlete's
// business (it may be a row they added and have not filled). A chip-on
// dropset is left out: its reps live in the chain, the parent cell is blank.
export type AmrapGateSet = Pick<DraftSet, 'amrapPrescribed' | 'reps' | 'timed'> &
  Partial<Pick<DraftSet, 'amrap' | 'dropset'>>;

export function amrapNeedsReps(set: AmrapGateSet | undefined): boolean {
  if (!set) return false;
  // #41: a set switched to «Timed» logs seconds, not reps.
  if (set.timed === true) return false;
  const isAmrap = set.amrapPrescribed || (set.amrap === true && !set.dropset);
  return isAmrap && set.reps.trim() === '';
}

/** The same gate addressed by position, for the completion legs — which hold
 *  indices, not the set. Out-of-range indices answer false: a set that is not
 *  there is not a set being blocked. */
export function amrapNeedsRepsAt(
  entries: ReadonlyArray<{ sets: ReadonlyArray<AmrapGateSet> }>,
  entryIdx: number,
  setIdx: number,
): boolean {
  return amrapNeedsReps(entries[entryIdx]?.sets[setIdx]);
}
